import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'

import { Splash } from './Splash'
import { useSession } from './SessionProvider'

/**
 * Route guards.
 *
 * `loading` is deliberately distinct from `authenticated`: redirecting while the
 * profile is still in flight flashes the sign-in screen at a user who is in fact
 * signed in, which is the single most common bug in this pattern.
 */
export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { authenticated, loading } = useSession()
  const location = useLocation()

  if (loading) return <Splash />
  if (!authenticated) {
    // The whole address, not just the path: `/trade/terminal?symbol=ETHUSDT`
    // must still open that market after sign-in.
    const from = `${location.pathname}${location.search}${location.hash}`
    return <Navigate to="/signin" replace state={{ from }} />
  }
  return <>{children}</>
}

/** Keeps a signed-in user out of the sign-in and sign-up screens. */
export function RequireAnonymous({ children }: { children: ReactNode }): JSX.Element {
  const { authenticated, loading } = useSession()

  if (loading) return <Splash />
  if (authenticated) return <Navigate to="/" replace />
  return <>{children}</>
}
